import type {ComponentStageData, PluginContext, ReadOnlyExtensionAPI} from '../../../types'
import {getSourceLayoutPointer, getStructurePointer} from '../../../conversionDal/utils/pointer'
import type {OrderLayoutModelHandlers} from '../types'
import {AutoResponsivenessMode} from '../../../../../constants/dsTypesConstants'
import type {CoreLogger} from '@wix/document-manager-core'
import {ReportableWarning} from '@wix/document-manager-utils'
import * as gridAdaptive from './grid/adaptive'
import * as gridFlush from './grid/flush'
import * as stackAdaptive from './stack/adaptive'
import * as stackFlush from './stack/flush'

export {postStageOperation} from './grid/flush'

interface HandlersMap {
    gridHandlers: OrderLayoutModelHandlers
    stackHandlers: OrderLayoutModelHandlers
}

const UNSUPPORTED_CONTAINER_LAYOUT_LOGGED_KEY = 'order.unsupportedContainerLayoutLogged'

export const getContainerLayoutType = ({
    conversionDal,
    componentPointer
}: Pick<ComponentStageData, 'conversionDal' | 'componentPointer'>): string | undefined => {
    const structurePointer = getStructurePointer({id: componentPointer.id})
    return conversionDal.get(getSourceLayoutPointer(structurePointer))?.containerLayout?.type
}

export const getLayoutModelHandlers = (
    containerLayoutType: string | undefined,
    {gridHandlers, stackHandlers}: HandlersMap,
    logger: CoreLogger,
    componentId: string,
    ctx: PluginContext
): OrderLayoutModelHandlers => {
    if (containerLayoutType === 'StackContainerLayout') {
        return stackHandlers
    }

    if (!containerLayoutType || containerLayoutType === 'GridContainerLayout') {
        return gridHandlers
    }

    const logged: Record<string, true> = ctx.get(UNSUPPORTED_CONTAINER_LAYOUT_LOGGED_KEY) ?? {}
    const dedupeKey = `${componentId}:${containerLayoutType}`
    if (!logged[dedupeKey]) {
        logger.captureError(
            new ReportableWarning({
                message: `No order handlers defined for container layout type: ${containerLayoutType}. Falling back to grid handlers.`,
                errorType: 'unsupportedContainerLayoutType',
                extras: {containerLayoutType, componentId}
            })
        )
        logged[dedupeKey] = true
        ctx.set(UNSUPPORTED_CONTAINER_LAYOUT_LOGGED_KEY, logged)
    }

    return gridHandlers
}

export const createGridHandlers = (readOnlyExtensionAPI: ReadOnlyExtensionAPI): OrderLayoutModelHandlers => ({
    [AutoResponsivenessMode.Adaptive]: {
        createOrderVirtualStructure: () => {},
        createOrderRealStructure: gridAdaptive.createOrderRealStructure(readOnlyExtensionAPI),
        positionItems: gridAdaptive.positionItems
    },
    [AutoResponsivenessMode.Flush]: {
        createOrderVirtualStructure: gridFlush.createOrderVirtualStructure(readOnlyExtensionAPI),
        createOrderRealStructure: gridFlush.orderRealStructure(readOnlyExtensionAPI),
        positionItems: gridFlush.positionItems
    }
})

export const createStackHandlers = (): OrderLayoutModelHandlers => ({
    [AutoResponsivenessMode.Adaptive]: {
        createOrderVirtualStructure: () => {},
        createOrderRealStructure: stackAdaptive.createOrderRealStructure,
        positionItems: stackAdaptive.positionItems
    },
    [AutoResponsivenessMode.Flush]: {
        createOrderVirtualStructure: () => {},
        createOrderRealStructure: stackFlush.createOrderRealStructure,
        positionItems: stackFlush.positionItems
    }
})
